import * as React from 'react'
import { useAuth } from '../auth/AuthProvider'
import { useUserContext } from './UserContextProvider'
import { api } from '../api/client'
import {
  clearMonoCssVars,
  clearColorToneVars,
  colorToneCssVars,
  deriveColorTones,
  deriveMono,
  isHexColor,
  luminance,
  monoCssVars,
  normalizeHex,
  MONO_DEFAULT_ACCENT,
  MONO_DEFAULT_BG_DARK,
  MONO_DEFAULT_BG_LIGHT,
  type MonoPreset,
  type MonoTokens,
  type SankeyStyle,
} from './monoTheme'

type Theme = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'
export type ColorTheme =
  | 'mono-flat'
  | 'mono'
  | 'color'
  | 'color-flat'
  | 'techy'
  | 'obsidian'
  | 'folio-bloom'
  | 'folio-oak'

export type CaisiboColorMode = 'mono' | 'color'

export const COLOR_THEMES: { id: ColorTheme; name: string; color: string; font: string }[] = [
  { id: 'mono-flat',    name: 'Caisibo Mono · Flat',  color: '#f5b82e', font: 'Schibsted Grotesk' },
  { id: 'mono',         name: 'Caisibo Mono',         color: '#f5b82e', font: 'Schibsted Grotesk' },
  { id: 'color',        name: 'Caisibo Color',        color: '#f5b82e', font: 'Schibsted Grotesk' },
  { id: 'color-flat',   name: 'Caisibo Color · Flat', color: '#f5b82e', font: 'Schibsted Grotesk' },
  { id: 'techy',        name: 'Techy',        color: '#1a9e8c', font: 'IBM Plex Sans'      },
  { id: 'obsidian',     name: 'Obsidian',     color: '#0d3b7a', font: 'Plus Jakarta Sans'  },
  { id: 'folio-bloom',  name: 'Folio · Bloom', color: '#c87e6c', font: 'Young Serif'       },
  { id: 'folio-oak',    name: 'Folio · Oak',   color: '#9a4630', font: 'Young Serif'       },
]

const COLOR_THEME_IDS: ReadonlySet<ColorTheme> = new Set(COLOR_THEMES.map((t) => t.id))
// The four Caisibo themes share one accent engine, the color studio, and the
// Sankey style option. They vary on two axes:
//   · family    → mono (single accent + textures) | color (derived hues)
//   · elevation → flat (surfaces == bg) | raised cards
const CAISIBO_THEME_IDS: ReadonlySet<ColorTheme> = new Set<ColorTheme>(['mono-flat', 'mono', 'color', 'color-flat'])
const FLAT_THEME_IDS: ReadonlySet<ColorTheme> = new Set<ColorTheme>(['mono-flat', 'color-flat'])
const COLOR_FAMILY_IDS: ReadonlySet<ColorTheme> = new Set<ColorTheme>(['color', 'color-flat'])
const DEFAULT_COLOR_THEME: ColorTheme = 'mono-flat'

function caisiboColorModeOf(ct: ColorTheme): CaisiboColorMode {
  return COLOR_FAMILY_IDS.has(ct) ? 'color' : 'mono'
}

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
  colorTheme: ColorTheme
  setColorTheme: (t: ColorTheme) => void
  // ── Caisibo customisation (mono + color families) ──
  caisiboActive: boolean
  caisiboFlat: boolean
  caisiboColorMode: CaisiboColorMode
  monoAccent: string
  monoBg: string
  monoTokens: MonoTokens
  setMonoAccent: (hex: string) => void
  setMonoBg: (hex: string) => void
  applyMonoPreset: (preset: MonoPreset) => void
  sankeyStyle: SankeyStyle
  setSankeyStyle: (style: SankeyStyle) => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

const STORAGE_KEY        = 'okr-theme'
const COLOR_STORAGE_KEY  = 'okr-color-theme'
const COLOR_RECONCILED_STORAGE_KEY = 'okr-color-theme-reconciled'
const ACCENT_STORAGE_KEY = 'okr-mono-accent'
const BG_DARK_STORAGE_KEY = 'okr-mono-bg-dark'
const BG_LIGHT_STORAGE_KEY = 'okr-mono-bg-light'
const SANKEY_STORAGE_KEY = 'okr-sankey-style'

interface ReconciledColorTheme {
  userId: string
  colorTheme: ColorTheme
}

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyMode(resolved: ResolvedTheme) {
  if (resolved === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

function applyColorTheme(ct: ColorTheme) {
  const el = document.documentElement
  el.setAttribute('data-color-theme', ct)
  // A single boolean attribute lets the shared Caisibo CSS (fonts, flat
  // background, sidebar treatment) target all four variants without listing them.
  if (CAISIBO_THEME_IDS.has(ct)) el.setAttribute('data-caisibo', '')
  else el.removeAttribute('data-caisibo')
  // The mono family drops every hue: this attribute scopes the global de-hue
  // safety net + texture vocabulary.
  el.toggleAttribute('data-caisibo-mono', CAISIBO_THEME_IDS.has(ct) && caisiboColorModeOf(ct) === 'mono')
  // The color family re-hues every status palette to a derived harmonized tone.
  el.toggleAttribute('data-caisibo-color', CAISIBO_THEME_IDS.has(ct) && caisiboColorModeOf(ct) === 'color')
  // The flat variants force every surface flush with the background.
  el.toggleAttribute('data-caisibo-flat', FLAT_THEME_IDS.has(ct))
}

function isColorTheme(v: unknown): v is ColorTheme {
  return typeof v === 'string' && COLOR_THEME_IDS.has(v as ColorTheme)
}

function isTheme(v: unknown): v is Theme {
  return v === 'light' || v === 'dark' || v === 'system'
}

function isSankeyStyle(v: unknown): v is SankeyStyle {
  return v === 'patterns' || v === 'colors'
}

function readStoredHex(key: string, fallback: string): string {
  const stored = localStorage.getItem(key)
  return isHexColor(stored) ? normalizeHex(stored, fallback) : fallback
}

function readReconciledColorTheme(): ReconciledColorTheme | null {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(COLOR_RECONCILED_STORAGE_KEY) ?? 'null')
    if (!stored || typeof stored !== 'object') return null
    const { userId, colorTheme } = stored as Partial<ReconciledColorTheme>
    return typeof userId === 'string' && isColorTheme(colorTheme) ? { userId, colorTheme } : null
  } catch {
    return null
  }
}

function markColorThemeReconciled(userId: string, colorTheme: ColorTheme) {
  localStorage.setItem(COLOR_RECONCILED_STORAGE_KEY, JSON.stringify({ userId, colorTheme }))
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { userDetail } = useUserContext()
  const userId = userDetail?.id

  const [theme, setThemeState] = React.useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isTheme(stored) ? stored : 'system'
  })

  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() => {
    if (theme === 'system') return getSystemTheme()
    return theme
  })

  const [colorTheme, setColorThemeState] = React.useState<ColorTheme>(() => {
    const stored = localStorage.getItem(COLOR_STORAGE_KEY)
    return isColorTheme(stored) ? stored : DEFAULT_COLOR_THEME
  })

  const [monoAccent, setMonoAccentState] = React.useState<string>(() =>
    readStoredHex(ACCENT_STORAGE_KEY, MONO_DEFAULT_ACCENT))
  const [monoBgDark, setMonoBgDarkState] = React.useState<string>(() =>
    readStoredHex(BG_DARK_STORAGE_KEY, MONO_DEFAULT_BG_DARK))
  const [monoBgLight, setMonoBgLightState] = React.useState<string>(() =>
    readStoredHex(BG_LIGHT_STORAGE_KEY, MONO_DEFAULT_BG_LIGHT))
  const [sankeyStyle, setSankeyStyleState] = React.useState<SankeyStyle>(() => {
    const stored = localStorage.getItem(SANKEY_STORAGE_KEY)
    return isSankeyStyle(stored) ? stored : 'patterns'
  })

  const hydratedUserId = React.useRef<string | null>(null)

  const persist = React.useCallback(
    (payload: Parameters<typeof api.users.updatePreferences>[0]) => {
      if (isAuthenticated && userId) {
        api.users.updatePreferences(payload).catch(() => {
          // Keep the local preference for this session.
        })
      }
    },
    [isAuthenticated, userId],
  )

  const persistColorTheme = React.useCallback(
    (
      nextColorTheme: ColorTheme,
      additionalPreferences: Parameters<typeof api.users.updatePreferences>[0] = {},
    ) => {
      if (!isAuthenticated || !userId) return
      api.users.updatePreferences({ colorTheme: nextColorTheme, ...additionalPreferences })
        .then(() => {
          // A newer choice may have been made while this request was in flight.
          if (localStorage.getItem(COLOR_STORAGE_KEY) === nextColorTheme) {
            markColorThemeReconciled(userId, nextColorTheme)
          }
        })
        .catch(() => {
          // Keep the local choice unreconciled so it can be retried later.
        })
    },
    [isAuthenticated, userId],
  )

  // Reconcile once per signed-in user. An unreconciled browser choice is newer
  // than the server snapshot and must not be replaced by a delayed response.
  // Once reconciled, the server remains authoritative so changes from another
  // browser still load here on the next session.
  React.useEffect(() => {
    if (!userDetail || hydratedUserId.current === userDetail.id) return
    hydratedUserId.current = userDetail.id

    const serverColor = userDetail.colorTheme
    const serverMode = userDetail.themeMode
    const storedColor = localStorage.getItem(COLOR_STORAGE_KEY)
    const localColor = isColorTheme(storedColor) ? storedColor : null
    const reconciledColor = readReconciledColorTheme()
    const localColorBelongsToUser = !reconciledColor || reconciledColor.userId === userDetail.id
    const localColorIsUnreconciled = Boolean(
      localColor &&
      localColorBelongsToUser &&
      (!reconciledColor || reconciledColor.colorTheme !== localColor),
    )

    if (localColorIsUnreconciled && localColor) {
      if (localColor !== colorTheme) setColorThemeState(localColor)
      if (serverColor === localColor) markColorThemeReconciled(userDetail.id, localColor)
      else persistColorTheme(localColor)
    } else if (isColorTheme(serverColor)) {
      localStorage.setItem(COLOR_STORAGE_KEY, serverColor)
      markColorThemeReconciled(userDetail.id, serverColor)
      if (serverColor !== colorTheme) setColorThemeState(serverColor)
    } else if (!localColor || !localColorBelongsToUser) {
      localStorage.setItem(COLOR_STORAGE_KEY, DEFAULT_COLOR_THEME)
      markColorThemeReconciled(userDetail.id, DEFAULT_COLOR_THEME)
      if (colorTheme !== DEFAULT_COLOR_THEME) setColorThemeState(DEFAULT_COLOR_THEME)
    }
    if (isTheme(serverMode) && serverMode !== theme) {
      localStorage.setItem(STORAGE_KEY, serverMode)
      setThemeState(serverMode)
    }
    if (isHexColor(userDetail.monoAccent)) {
      const v = normalizeHex(userDetail.monoAccent, MONO_DEFAULT_ACCENT)
      localStorage.setItem(ACCENT_STORAGE_KEY, v)
      setMonoAccentState(v)
    }
    if (isHexColor(userDetail.monoBgDark)) {
      const v = normalizeHex(userDetail.monoBgDark, MONO_DEFAULT_BG_DARK)
      localStorage.setItem(BG_DARK_STORAGE_KEY, v)
      setMonoBgDarkState(v)
    }
    if (isHexColor(userDetail.monoBgLight)) {
      const v = normalizeHex(userDetail.monoBgLight, MONO_DEFAULT_BG_LIGHT)
      localStorage.setItem(BG_LIGHT_STORAGE_KEY, v)
      setMonoBgLightState(v)
    }
    if (isSankeyStyle(userDetail.sankeyStyle) && userDetail.sankeyStyle !== sankeyStyle) {
      localStorage.setItem(SANKEY_STORAGE_KEY, userDetail.sankeyStyle)
      setSankeyStyleState(userDetail.sankeyStyle)
    }
  }, [userDetail, theme, colorTheme, sankeyStyle, persistColorTheme])

  const setTheme = React.useCallback(
    (t: Theme) => {
      localStorage.setItem(STORAGE_KEY, t)
      setThemeState(t)
      persist({ themeMode: t })
    },
    [persist],
  )

  const setColorTheme = React.useCallback(
    (t: ColorTheme) => {
      // Explicit input makes the browser value newer than the last server
      // snapshot, even when the user re-selects the currently visible theme.
      localStorage.removeItem(COLOR_RECONCILED_STORAGE_KEY)
      localStorage.setItem(COLOR_STORAGE_KEY, t)
      applyColorTheme(t)
      // Crossing the mono↔color family boundary picks a sensible Sankey default
      // (mono → textures, color → hues); an explicit toggle still overrides it.
      let nextSankey: SankeyStyle | null = null
      if (CAISIBO_THEME_IDS.has(t) && caisiboColorModeOf(t) !== caisiboColorModeOf(colorTheme)) {
        nextSankey = caisiboColorModeOf(t) === 'color' ? 'colors' : 'patterns'
        localStorage.setItem(SANKEY_STORAGE_KEY, nextSankey)
        setSankeyStyleState(nextSankey)
      }
      setColorThemeState(t)
      persistColorTheme(t, nextSankey ? { sankeyStyle: nextSankey } : {})
    },
    [persistColorTheme, colorTheme],
  )

  const setMonoAccent = React.useCallback(
    (hex: string) => {
      const v = normalizeHex(hex, MONO_DEFAULT_ACCENT)
      localStorage.setItem(ACCENT_STORAGE_KEY, v)
      setMonoAccentState(v)
      persist({ monoAccent: v })
    },
    [persist],
  )

  // Stores the background into the slot matching its own luminance (light colors
  // → light slot, dark → dark slot) and aligns the app mode so the picked
  // background becomes the active one — no matter which mode was active before.
  const setMonoBg = React.useCallback(
    (hex: string) => {
      const isDark = luminance(normalizeHex(hex, MONO_DEFAULT_BG_DARK)) < 0.5
      const v = normalizeHex(hex, isDark ? MONO_DEFAULT_BG_DARK : MONO_DEFAULT_BG_LIGHT)
      const mode: Theme = isDark ? 'dark' : 'light'
      if (isDark) {
        localStorage.setItem(BG_DARK_STORAGE_KEY, v)
        setMonoBgDarkState(v)
      } else {
        localStorage.setItem(BG_LIGHT_STORAGE_KEY, v)
        setMonoBgLightState(v)
      }
      localStorage.setItem(STORAGE_KEY, mode)
      setThemeState(mode)
      persist({ ...(isDark ? { monoBgDark: v } : { monoBgLight: v }), themeMode: mode })
    },
    [persist],
  )

  const applyMonoPreset = React.useCallback(
    (preset: MonoPreset) => {
      const accent = normalizeHex(preset.accent, MONO_DEFAULT_ACCENT)
      localStorage.setItem(ACCENT_STORAGE_KEY, accent)
      setMonoAccentState(accent)

      const payload: Parameters<typeof api.users.updatePreferences>[0] = {
        monoAccent: accent,
        themeMode: preset.mode,
      }
      const setSlot = (slot: 'dark' | 'light', hex: string) => {
        const v = normalizeHex(hex, slot === 'dark' ? MONO_DEFAULT_BG_DARK : MONO_DEFAULT_BG_LIGHT)
        localStorage.setItem(slot === 'dark' ? BG_DARK_STORAGE_KEY : BG_LIGHT_STORAGE_KEY, v)
        if (slot === 'dark') {
          setMonoBgDarkState(v)
          payload.monoBgDark = v
        } else {
          setMonoBgLightState(v)
          payload.monoBgLight = v
        }
      }

      setSlot(preset.mode, preset.bg)
      // A preset may theme the opposite mode too (e.g. E Ink: paper ↔ reversed).
      if (preset.bgAlt) setSlot(preset.mode === 'dark' ? 'light' : 'dark', preset.bgAlt)

      localStorage.setItem(STORAGE_KEY, preset.mode)
      setThemeState(preset.mode)
      persist(payload)
    },
    [persist],
  )

  const setSankeyStyle = React.useCallback(
    (style: SankeyStyle) => {
      localStorage.setItem(SANKEY_STORAGE_KEY, style)
      setSankeyStyleState(style)
      persist({ sankeyStyle: style })
    },
    [persist],
  )

  React.useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme
    setResolvedTheme(resolved)
  }, [theme])

  const caisiboActive = CAISIBO_THEME_IDS.has(colorTheme)
  const caisiboFlat = FLAT_THEME_IDS.has(colorTheme)
  const caisiboColorMode = caisiboColorModeOf(colorTheme)
  const monoBg = resolvedTheme === 'dark' ? monoBgDark : monoBgLight
  // Key the derivation on the actual background luminance, not the theme mode —
  // so a light background chosen while in "dark" mode (or vice-versa) still gets
  // readable ink and the correct .dark class.
  const monoDark = luminance(monoBg) < 0.5
  const monoTokens = React.useMemo(
    () => deriveMono(monoAccent, monoBg, monoDark, caisiboFlat),
    [monoAccent, monoBg, monoDark, caisiboFlat],
  )
  const colorTones = React.useMemo(
    () => deriveColorTones(monoAccent, monoBg, monoDark),
    [monoAccent, monoBg, monoDark],
  )

  // The .dark class follows the effective background so `dark:` utilities + dark
  // CSS match the surface a Caisibo theme actually renders.
  const effectiveDark = caisiboActive ? monoDark : resolvedTheme === 'dark'
  React.useEffect(() => {
    applyMode(effectiveDark ? 'dark' : 'light')
  }, [effectiveDark])

  // Apply the color-theme attribute + (for any Caisibo theme) the derived inline
  // token overrides, plus (for the color family) the harmonized semantic tones.
  React.useEffect(() => {
    applyColorTheme(colorTheme)
    const el = document.documentElement
    if (CAISIBO_THEME_IDS.has(colorTheme)) {
      const vars = monoCssVars(monoTokens)
      for (const [key, value] of Object.entries(vars)) el.style.setProperty(key, value)
    } else {
      clearMonoCssVars(el)
    }
    if (COLOR_FAMILY_IDS.has(colorTheme)) {
      const vars = colorToneCssVars(colorTones)
      for (const [key, value] of Object.entries(vars)) el.style.setProperty(key, value)
    } else {
      clearColorToneVars(el)
    }
  }, [colorTheme, monoTokens, colorTones])

  React.useEffect(() => {
    if (theme !== 'system') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light')
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [theme])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        colorTheme,
        setColorTheme,
        caisiboActive,
        caisiboFlat,
        caisiboColorMode,
        monoAccent,
        monoBg,
        monoTokens,
        setMonoAccent,
        setMonoBg,
        applyMonoPreset,
        sankeyStyle,
        setSankeyStyle,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

/**
 * Non-throwing variant for low-level UI (e.g. StatusBadge) that may render
 * outside a ThemeProvider (tests, isolated stories). Returns null when absent.
 */
export function useThemeOptional(): ThemeContextValue | null {
  return React.useContext(ThemeContext)
}
